import dayjs from "dayjs";
import { ChannelkartNetworkGet, ChannelkartNetworkPut } from "../../services/networkService";
import { EnvironmentRegistry } from "../../services/registry/environmentRegistry";
import { getAccessToken, getLob } from "../../services/authenticationService";

export const fetchInterfaceData = async (
  interfaceType: string,
  fromDate: string,
  toDate: string
) => {
  try {
    let rawRows: any[] = [];

    const type = interfaceType.toLowerCase();

    if (type === "orders") {
      rawRows = await fetchOrders(fromDate, toDate);
    }

    if (type === "payments") {
      rawRows = await fetchPayments(fromDate, toDate);
    }

    if (type === "settlements") {
      rawRows = await fetchSettlements();
    }

    if (!rawRows.length) return [];

    const toDateIsToday = dayjs(toDate).isSame(dayjs(), "day");
    let activityMap = new Map<string, Set<string>>();

    if (toDateIsToday) {
      const activityFrom = dayjs(fromDate).subtract(1, "day").format("YYYY-MM-DD") + " 21:00:00";
      const activityTo = dayjs(toDate).format("YYYY-MM-DD") + " 20:59:59";

      const encodedActivityFrom = encodeURIComponent(`startTime[gte]:${activityFrom}`);
      const encodedActivityTo = encodeURIComponent(`startTime[lte]:${activityTo}`);

      const activityRes = await ChannelkartNetworkGet(
        `/userActivity?filter=activity:dayEnd and ${encodedActivityFrom} and ${encodedActivityTo}`
      );

      const activityList = activityRes.data.features ?? [];

      activityList.forEach((a: any) => {
        if (!a.startTime) return;
        const key = dayjs(a.startTime).format("YYYY-MM-DD");

        if (!activityMap.has(key)) {
          activityMap.set(key, new Set());
        }

        activityMap.get(key)!.add(a.loginId);
      });
    }

    const enriched: any[] = [];

    for (const item of rawRows) {
      const login = item.loginId || item.loginid || item.created_by;
      const creationDate = dayjs(item.creationTime || item.creation_time).format("YYYY-MM-DD");
      const today = dayjs().format("YYYY-MM-DD");

      let closingDate: string | null = null;

      if (!toDateIsToday) {
        closingDate = creationDate;
      } else if (creationDate < today) {
        closingDate = creationDate;
      } else if (creationDate > today) {
        continue;
      } else {
        const loginSet = activityMap.get(today);
        if (!loginSet || !loginSet.has(login)) continue;
        closingDate = today;
      }

      let designation = item.designation || item.extendedAttributes?.designation || "";

      if (type === "payments" && login) {
        try {
          const userRes = await ChannelkartNetworkGet(`/users?filter=id:${login}`);
          designation = userRes?.data?.features?.[0]?.designation?.[0] || "";
        } catch (err) {
          designation = "";
        }
      }

      enriched.push({
        ...item,
        closingDate,
        designation
      });
    }


    const uniqueMap = new Map();

    for (const row of enriched) {
      // const key = (row.loginId || row.loginid || row.created_by) + "_" + row.closingDate;
      const key = (row.loginId || row.loginid || row.created_by) + "_" + row.closingDate + "_" + (row.beat || row.extendedAttributes?.beat);

      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, {
          ...row,
          referenceNumbers: type == "orders" ? ((row.orderNumber) ? [row.orderNumber] : []) : ((row.reference_number) ? [row.reference_number] : [])
        });
      } else {
        const existing = uniqueMap.get(key);

        const isPending =
          existing.status?.toLowerCase() === "pending" ||
          row.status?.toLowerCase() === "pending";

        existing.status = isPending ? "PENDING" : (existing.status.toUpperCase() || row.status.toLowerCase());

        if (type == "orders" && row.orderNumber && !existing.referenceNumbers.includes(row.orderNumber)) {
          existing.referenceNumbers.push(row.orderNumber);
        }

        if (type == "payments" && row.reference_number && !existing.referenceNumbers.includes(row.reference_number)) {
          existing.referenceNumbers.push(row.reference_number);
        }

        uniqueMap.set(key, existing);
      }
    }

    return transformRows(Array.from(uniqueMap.values()));
  }
  catch (err) {
    console.error("Fetch error: ", err);
  }
};


const fetchOrders = async (fromDate: string, toDate: string) => {
  const formattedFrom = dayjs(fromDate).format("YYYY-MM-DD HH:mm:ss");
  const formattedTo = dayjs(toDate).format("YYYY-MM-DD HH:mm:ss");

  const encodedFrom = encodeURIComponent(`creationTime[gte]:${formattedFrom}`);
  const encodedTo = encodeURIComponent(`creationTime[lte]:${formattedTo}`);

  const size = 100;
  let page = 0;
  let allRows: any[] = [];

  while (true) {
    const res = await ChannelkartNetworkGet(
      `/v2/orders` +
      `?filter=${encodedFrom} and ${encodedTo}` +
      `&page=${page}` +
      `&size=${size}`
    );

    const rows = res.data?.features ?? [];

    if (rows.length === 0) break;

    allRows.push(...rows);

    if (rows.length < size) break;
    page++;
  }

  return allRows
    .map((row: any) => {
      const transformedStatus = normalizeOrderStatus(row.status);

      if (!transformedStatus) return null;

      return {
        ...row,
        actualStatus: row.status.toUpperCase(),
        status: transformedStatus
      };
    })
    .filter(Boolean);
};


const fetchPayments = async (fromDate: string, toDate: string) => {
  const env = EnvironmentRegistry.getEnvironment();

  const formattedFrom = dayjs(fromDate).format("YYYY-MM-DDTHH:mm:ss");
  const formattedTo = dayjs(toDate).format("YYYY-MM-DDTHH:mm:ss");

  const size = 100;
  let page = 0;
  let allRows: any[] = [];

  while (true) {
    const res = await ChannelkartNetworkGet(
      `https://credits-${env}.salescode.ai/api/v1/outlets/collections` +
      `?startDate=${formattedFrom}` +
      `&endDate=${formattedTo}` +
      `&page=${page}` +
      `&size=${size}`,
      undefined,
      { env: env ?? "" }
    );

    const rows = res.data ?? [];

    if (rows.length === 0) break;

    allRows.push(...rows);
    if (rows.length < size) break;
    page++;
  }

  return allRows
    .map((row: any) => {
      const transformedStatus = normalizePaymentStatus(row.push_status);

      if (!transformedStatus) return null;

      return {
        ...row,
        actualStatus: row.push_status.toUpperCase(),
        status: transformedStatus
      };
    })
    .filter(Boolean);
};


const fetchSettlements = async () => {
  const authContext = localStorage.getItem("authContext");
  const loginId = authContext ? JSON.parse(authContext).user.loginId : null;

  const userRes = await ChannelkartNetworkGet(`/users?filter=id:${loginId}`);
  const supplier =
    userRes?.data?.features?.[0]?.extendedAttributes?.depoLocation ?? "";

  if (!supplier) return [];

  const res = await ChannelkartNetworkGet(
    `/v3/sales/dmsInvoice?filter=loginId:${supplier}`
  );

  return res.data.features ?? res.data.content ?? [];
};

const normalizeOrderStatus = (rawStatus?: string) => {
  const status = rawStatus?.toUpperCase();

  if (!status) return null;

  if (status === "INPROGRESS") {
    return "INPROGRESS";
  }

  if (status === "SENTTOBASIS") {
    return "SUCCESS";
  }

  if (status === "PENDING" || status === "ONHOLD") {
    return "PENDING";
  }
  return null;
};

const normalizePaymentStatus = (rawStatus?: string) => {
  const status = rawStatus?.toUpperCase();

  if (!status) return null;

  if (status === "IN_PROGRESS") {
    return "INPROGRESS";
  }

  if (status === "PUSHED") {
    return "SUCCESS";
  }

  if (status === "PENDING" || status === "SUCCESS") {
    return status.toUpperCase();
  }

  return null;
};


export const transformRows = (features: any[]) => {
  return features.map((feature: any) => ({
    id: feature.id || feature.collection_id || "",
    depot: feature.supplier || "",
    uniqueSalesRepId:
      feature.loginId || feature.loginid || feature.created_by || "",
    route: feature.beat || feature.extendedAttributes?.beat || "",
    salesmanType:
      feature.designation || feature.extendedAttributes?.designation || "",
    closingDate: feature.closingDate
      ? dayjs(feature.closingDate).format("YYYY-MM-DD")
      : "",
    receivingDate:
      feature.creationTime || feature.creation_time
        ? dayjs(feature.creationTime ?? feature.creation_time).format(
          "YYYY-MM-DD"
        )
        : "",
    actualStatus: feature.actualStatus || "",
    status: feature.status || "PENDING",
    beat: feature.beat || "",
    creationTime: feature.creationTime || "",
    outletCode:
      feature.outlet_code ||
      feature.outletCode ||
      feature.outletcode ||
      "",
    referenceNumbers: feature.referenceNumbers
  }));
};



export const syncSingleRecord = async (interfaceType: string, startDate: string, endDate: string, salesRoute: string, depot: string, status: string, idArray: string, isSync: boolean) => {
  try {
    const body = buildRequestBody(interfaceType, startDate, endDate, salesRoute, depot, status, idArray, isSync);
    const apiUrl = getSyncApiUrl(interfaceType);

    const res = await ChannelkartNetworkPut(apiUrl, body);

    return { success: res?.data.features[0].status === "success" };
  } catch (err) {
    console.error("Sync error: ", err);
    return { success: false, error: err };
  }
};

const getSyncApiUrl = (interfaceType: string) => {
  return "/jenkins/ccbcsa_integration_job/build-with-params";
};

const buildRequestBody = (interfaceType: string, startDate: string, endDate: string, salesRoute: string, depot: string, status: string, idArray: string, isSync: boolean) => {
  const env = EnvironmentRegistry.getEnvironment();
  const LOB = getLob();
  const token = getAccessToken();

  switch (interfaceType.toLowerCase()) {
    case "orders": {
      const formattedFrom = dayjs(startDate).format("YYYY-MM-DD HH:mm:ss");
      const formattedTo = dayjs(endDate).format("YYYY-MM-DD HH:mm:ss");
      const payload: any = {
        MODE: "OE60",
        PUSH_MODE: "API_DATA",
        START_TIME: formattedFrom,
        END_TIME: formattedTo,
        API_BASE_URL: `https://${env}.salescode.ai`,
        API_TOKEN: token,
        LOB,
        STATUS: status.toUpperCase(),
        SYNC: isSync,
        RESYNC: !isSync
      };

      if (idArray && idArray.trim() !== "") {
        payload.ORDER_IDS = idArray;
      }

      if (salesRoute) {
        payload.BEAT = salesRoute;
      }

      if (depot) {
        payload.SUPPLIER_ID = depot;
      }

      return payload;
    }

    case "settlements":
      return {
        MODE: "RS90",
        SUPPLIER_ID: depot,
        RS90_API_URL: `https://${env}.salescode.ai/v3/sales/dmsInvoice`,
        RS90_API_TOKEN: token,
        RS90_API_LOB: LOB,
        RS90_START_TIME: "2025-10-01 00:00:00",
        RS90_END_TIME: "2025-10-20 00:00:00"
      };

    case "payments": {
      const formattedFrom = dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss");
      const formattedTo = dayjs(endDate).format("YYYY-MM-DDTHH:mm:ss");
      const payload: any = {
        MODE: "AR41",
        AR41_START_TIME: formattedFrom,
        AR41_END_TIME: formattedTo,
        SUPPLIER: depot,
        AR41_API_BASE_URL: `https://credits-${env}.salescode.ai`,
        AR41_API_TOKEN: token,
        AR41_API_LOB: LOB,
        SYNC_COLLECTION: false,
        AR41_STATUS: "IN_PROGRESS",
        AR41_API_ENV: env
      };

      if (idArray && idArray.trim() !== "") {
        payload.REFERENCE_NUMBERS = idArray;
        payload.SYNC_COLLECTION = true;
      }

      return payload
    }

    default:
      return {};
  }
};

export const changeEntryStatus = async (interfaceType: string, referenceNumbers: string[]) => {
  const env = EnvironmentRegistry.getEnvironment();
  const LOB = getLob();
  const token = getAccessToken();

  try {
    switch (interfaceType.toLowerCase()) {
      case "orders":
        let orderBatches: string[][] = [];

        for (let i = 0; i < referenceNumbers.length; i += 100) {
          orderBatches.push(referenceNumbers.slice(i, i + 100));
        }

        for (const batch of orderBatches) {
          const body = batch.map(orderNumber => ({
            orderNumber,
            status: "INPROGRESS",
          }));

          await ChannelkartNetworkPut(
            `https://${env}.salescode.ai/orders/status/batch`,
            body
          );
        }
        break;
      case "payments":
        for (let i = 0; i < referenceNumbers.length; i += 100) {
          const batch = referenceNumbers.slice(i, i + 100);
          const body = {
            referenceNumbers: batch,
            status: "IN_PROGRESS"
          };

          await ChannelkartNetworkPut(
            `https://credits-${env}.salescode.ai/api/v1/outlets/collections/mark-pushed`,
            body,
            { ENV: env ?? "" }
          );
        }
        break;
      default:
        return;
    }
  } catch (err) {
    
  }
};
